import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useState } from "react";

import { PasswordResetConfirmDialog } from "./PasswordResetConfirmDialog";

export type ResetPasswordComponentEvent =
  "back-to-login" | "request-password-reset" | "change-language";

export interface ResetPasswordComponentState {
  language: SupportedLanguage;
}

const languageOptions: {
  code: SupportedLanguage;
  label: string;
}[] = [
  { code: "de", label: "german" },
  { code: "en", label: "english" },
];

export const ResetPasswordPage: PimoReactComponent<
  ResetPasswordComponentState,
  ResetPasswordComponentEvent,
  { email?: string; language?: SupportedLanguage }
> = ({ fireEvent, language = "en" }) => {
  const t = (key: string, fallback: string) => {
    try {
      const msg = getLocalizedMessage(language, key);
      return msg && msg !== key ? msg : fallback;
    } catch {
      return fallback;
    }
  };

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleBackToLogin = () => {
    fireEvent?.("back-to-login");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError(
        t("auth.resetPassword.errors.emailRequired", "Email is required.")
      );
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError(
        t(
          "auth.resetPassword.errors.emailInvalid",
          "Please enter a valid email address."
        )
      );
      return;
    }

    setEmailError("");
    fireEvent?.("request-password-reset", { email: trimmed.toLowerCase() });
    setShowConfirmDialog(true);
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#e4e9fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "-8px",
          p: "20px",
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 800,
            width: "100%",
            borderRadius: "20px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            p: { xs: "32px 24px", sm: "48px 48px 40px 48px" },
            mx: "auto",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Logo row */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
              position: "relative",
            }}
          >
            <img
              src="/pimo-large.svg"
              alt="Pimo Logo"
              style={{
                height: 45,
                width: 125,
                display: "block",
                objectFit: "contain",
              }}
            />

            <FormControl size="small" sx={{ minWidth: 0 }}>
              <Select
                value={language}
                onChange={(e) => {
                  const newLang = e.target.value as SupportedLanguage;
                  fireEvent?.("change-language", { language: newLang });
                }}
                displayEmpty
                sx={{
                  fontFamily:
                    "'Inter', Arial, 'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI Emoji', sans-serif",
                  fontSize: "0.95rem",
                  borderRadius: "6px",
                  height: 34,
                  minWidth: 118,
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    padding: "6px 42px 6px 12px !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #bbb",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bbb",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bbb",
                  },
                  "& .MuiSelect-icon": {
                    right: 12,
                  },
                  background: "#fff",
                }}
                inputProps={{
                  "aria-label": t("userMenu.language", "Language"),
                }}
              >
                {languageOptions.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {t(`userMenu.${option.label}`, option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Form section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: "2.2rem",
                fontWeight: 500,
                mb: 2,
                mt: 0,
                color: "#222",
                textAlign: "center",
                width: "100%",
              }}
            >
              {t("auth.resetPassword.title", "Reset Password")}
            </Typography>

            <Typography
              sx={{
                color: "#666",
                fontSize: "1rem",
                textAlign: "center",
                mb: 4,
                width: "100%",
                lineHeight: 1.5,
              }}
            >
              {t(
                "auth.resetPassword.description",
                "Enter your email address and we'll send you a link to reset your password."
              )}
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Form group — max-width 350px matching FTL */}
              <Box sx={{ width: "100%", maxWidth: 350, mb: 3 }}>
                <Typography
                  component="label"
                  htmlFor="reset-email"
                  sx={{
                    display: "block",
                    textAlign: "left",
                    mb: 1,
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#222",
                  }}
                >
                  {t("auth.resetPassword.labels.emailAddress", "Email Address")}
                </Typography>
                <TextField
                  id="reset-email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  placeholder="Enter your email address"
                  variant="outlined"
                  fullWidth
                  type="email"
                  error={!!emailError}
                  helperText={emailError}
                  slotProps={{
                    input: {
                      style: { fontSize: "1rem" },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ddd",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ddd",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3ec6ff",
                        borderWidth: 2,
                        boxShadow: "0 0 0 3px rgba(62,198,255,0.1)",
                      },
                      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ff6b6b",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      background: "#fff",
                      padding: "12px 16px",
                    },
                  }}
                  autoFocus
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                sx={{
                  width: "100%",
                  maxWidth: 350,
                  py: "14px",
                  fontSize: "1.1rem",
                  borderRadius: "8px",
                  bgcolor: "#3ec6ff",
                  color: "#fff",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  mb: 2,
                  ":hover": {
                    bgcolor: "#1da1f2",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                    transform: "translateY(-1px)",
                  },
                  ":active": { transform: "translateY(0)" },
                }}
              >
                {t("auth.resetPassword.buttons.sendLink", "Send Reset Link")}
              </Button>
            </Box>
          </Box>

          {/* Back to Login link — matches FTL .pimo-back-link style */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography
              component="a"
              onClick={handleBackToLogin}
              sx={{
                color: "#1da1f2",
                fontWeight: 500,
                fontSize: "0.95rem",
                cursor: "pointer",
                textDecoration: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                display: "inline-block",
                "&:hover": {
                  bgcolor: "rgba(29,161,242,0.1)",
                  textDecoration: "none",
                },
                "&::before": { content: '"← "' },
              }}
            >
              {t("auth.resetPassword.buttons.backToLogin", "Back to Login")}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <PasswordResetConfirmDialog
        open={showConfirmDialog}
        language={language}
        onClose={handleBackToLogin}
      />
    </>
  );
};
